'use client';

import { createElement, useCallback, useEffect, useState } from 'react';
import { SavedJobToastAction } from '@/components/saved-job-success-toast';
import {
  getSaveOpportunityTrackingProperties,
  useOpportunityTracking,
} from '@/hooks/use-opportunity-tracking';
import { apis } from '@/libs-service';
import { OPPORTUNITY_TRACKING_EVENTS } from '@/services/opportunity-tracking';
import type { SourceScreen } from '@/services/opportunity-tracking/source-screen';
import {
  LIST_SAVED_JOB_ENDPOINT,
  SAVED_JOBS_PAGE_SIZE,
  SAVED_JOB_ENDPOINT,
  UNSAVED_JOB_ENDPOINT,
} from './constants';
import { getJobDetail } from './index';
import type {
  JobPagination,
  JobSummary,
  SavedJobCheckResponse,
  SavedJobMutationResponse,
  SavedJobRequest,
  SavedJobsResponse,
} from './model';

type UseSavedJobsOptions = {
  enabled?: boolean;
  fetchOnMount?: boolean;
  hydrateDetails?: boolean;
  limit?: number;
  page?: number;
  sourceScreen?: SourceScreen;
};

type ToggleSavedJobOptions = {
  sourceScreen?: SourceScreen;
};

type CheckSavedJobsOptions = {
  replace?: boolean;
  signal?: AbortSignal;
};

const SAVED_JOB_TOAST_ID = 'saved-job-feedback';
const SAVED_JOB_TOAST_DURATION = 3000;

async function loadToast() {
  try {
    return (await import('sonner')).toast;
  } catch {
    return null;
  }
}

function getPositiveInteger(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isInteger(value) && value > 0
    ? value
    : fallback;
}

function getNonNegativeInteger(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0
    ? value
    : fallback;
}

function getSavedJobs(
  response: SavedJobsResponse | JobSummary[]
): JobSummary[] {
  if (Array.isArray(response)) return response;

  return Array.isArray(response.data) ? response.data : [];
}

function getSavedJobsPagination(
  response: SavedJobsResponse | JobSummary[],
  requestedPage: number,
  requestedLimit: number,
  itemCount: number
): JobPagination {
  const responsePagination = Array.isArray(response)
    ? undefined
    : response.meta?.pagination;
  const pageSize = getPositiveInteger(
    responsePagination?.pageSize ?? responsePagination?.limit,
    requestedLimit
  );
  const total = getNonNegativeInteger(
    responsePagination?.total ?? responsePagination?.totalItems,
    itemCount
  );
  const totalPages = getPositiveInteger(
    responsePagination?.totalPages ?? responsePagination?.totalPage,
    Math.max(1, Math.ceil(total / pageSize))
  );

  return {
    page: Math.min(
      getPositiveInteger(responsePagination?.page, requestedPage),
      totalPages
    ),
    pageSize,
    total,
    totalPages,
  };
}

function normalizeSavedJob(job: JobSummary): JobSummary {
  return {
    ...job,
    applicantSignal: job.applicantSignal ?? {
      badge: '',
      color: '',
      value: '',
    },
    badge: job.badge ?? {
      color: '',
      content: '',
      icon: '',
      type: 'no_badge',
    },
    company: job.company ?? {
      logo: '',
      name: '',
    },
    workingLocations: Array.isArray(job.workingLocations)
      ? job.workingLocations
      : [],
  };
}

async function hydrateSavedJob(job: JobSummary): Promise<JobSummary> {
  const normalizedJob = normalizeSavedJob(job);

  if (!job.id || (job.jobTitle && job.company?.name)) {
    return normalizedJob;
  }

  try {
    const response = await getJobDetail(job.id);

    return normalizeSavedJob(response.data);
  } catch {
    return normalizedJob;
  }
}

export function useSavedJobs({
  enabled = true,
  fetchOnMount = true,
  hydrateDetails = false,
  limit,
  page,
  sourceScreen,
}: UseSavedJobsOptions = {}) {
  const { trackOpportunityEvent } = useOpportunityTracking();
  const requestedLimit = limit ?? SAVED_JOBS_PAGE_SIZE;
  const requestedPage = page ?? 1;
  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState(enabled && fetchOnMount);
  const [savedJobIds, setSavedJobIds] = useState<Set<string>>(() => new Set());
  const [savedJobs, setSavedJobs] = useState<JobSummary[]>([]);
  const [pagination, setPagination] = useState<JobPagination>({
    page: requestedPage,
    pageSize: requestedLimit,
    total: 0,
    totalPages: 1,
  });
  const [pendingJobId, setPendingJobId] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await apis.jobSeeker.get<SavedJobsResponse>(
        LIST_SAVED_JOB_ENDPOINT,
        limit === undefined && page === undefined
          ? undefined
          : { limit: requestedLimit, page: requestedPage }
      );
      const listedJobs = getSavedJobs(response);
      const jobs = hydrateDetails
        ? await Promise.all(listedJobs.map(hydrateSavedJob))
        : listedJobs.map(normalizeSavedJob);

      setSavedJobs(jobs);
      setSavedJobIds(new Set(jobs.map(job => job.id)));
      setPagination(
        getSavedJobsPagination(
          response,
          requestedPage,
          requestedLimit,
          listedJobs.length
        )
      );
    } catch (nextError) {
      setError(
        nextError instanceof Error
          ? nextError
          : new Error('Không thể tải việc làm đã lưu.')
      );
    } finally {
      setIsLoading(false);
    }
  }, [hydrateDetails, limit, page, requestedLimit, requestedPage]);

  const checkSavedJobs = useCallback(
    async (
      jobIds: string[],
      { replace = false, signal }: CheckSavedJobsOptions = {}
    ) => {
      const requestedJobIds = [...new Set(jobIds.filter(Boolean))];

      if (requestedJobIds.length === 0) {
        setSavedJobIds(new Set());

        return;
      }

      setError(null);

      try {
        const response = await apis.jobSeekerInfo.post<
          SavedJobCheckResponse,
          SavedJobRequest
        >(
          LIST_SAVED_JOB_ENDPOINT,
          { jobIds: requestedJobIds },
          { responseType: 'json', signal }
        );

        if (signal?.aborted) return;

        const checkedSavedJobIds = new Set(
          response.data.filter(job => job.isSaved).map(job => job.jobId)
        );

        setSavedJobIds(currentIds => {
          if (replace) return checkedSavedJobIds;

          const nextIds = new Set(currentIds);

          requestedJobIds.forEach(jobId => nextIds.delete(jobId));
          checkedSavedJobIds.forEach(jobId => nextIds.add(jobId));

          return nextIds;
        });
      } catch (nextError) {
        if (signal?.aborted) return;

        setError(
          nextError instanceof Error
            ? nextError
            : new Error('Không thể kiểm tra việc làm đã lưu.')
        );
      }
    },
    []
  );

  /* eslint-disable react-hooks/set-state-in-effect -- Clear user-scoped saved-job state when authentication is disabled. */
  useEffect(() => {
    if (!enabled) {
      setError(null);
      setIsLoading(false);
      setSavedJobIds(new Set());
      setSavedJobs([]);
      setPagination({
        page: requestedPage,
        pageSize: requestedLimit,
        total: 0,
        totalPages: 1,
      });

      return;
    }

    if (!fetchOnMount) {
      return;
    }

    void refetch();
  }, [enabled, fetchOnMount, refetch, requestedLimit, requestedPage]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const toggleSavedJob = useCallback(
    async (
      job: Pick<JobSummary, 'id' | 'jobTitle'>,
      options: ToggleSavedJobOptions = {}
    ) => {
      const { id: jobId, jobTitle } = job;
      const trackingSourceScreen = options.sourceScreen ?? sourceScreen;
      const wasSaved = savedJobIds.has(jobId);
      const toastPromise = loadToast();

      setPendingJobId(jobId);
      setSavedJobIds(currentIds => {
        const nextIds = new Set(currentIds);

        if (wasSaved) {
          nextIds.delete(jobId);
        } else {
          nextIds.add(jobId);
        }

        return nextIds;
      });

      try {
        if (wasSaved) {
          await apis.jobSeekerInfo.post<
            SavedJobMutationResponse,
            SavedJobRequest
          >(UNSAVED_JOB_ENDPOINT, { jobIds: [jobId] });
          void toastPromise.then(toast => {
            toast?.success('Đã gỡ việc làm khỏi danh sách đã lưu.', {
              action: null,
              duration: SAVED_JOB_TOAST_DURATION,
              id: SAVED_JOB_TOAST_ID,
            });
          });
        } else {
          await apis.jobSeekerInfo.post<
            SavedJobMutationResponse,
            SavedJobRequest
          >(SAVED_JOB_ENDPOINT, { jobIds: [jobId] });

          if (trackingSourceScreen) {
            void trackOpportunityEvent({
              eventName: OPPORTUNITY_TRACKING_EVENTS.save,
              jobId,
              properties:
                getSaveOpportunityTrackingProperties(trackingSourceScreen),
            });
          }

          const message = jobTitle
            ? `Đã lưu việc ${jobTitle}`
            : 'Đã lưu việc làm';

          void toastPromise.then(toast => {
            if (!toast) return;

            toast.success(message, {
              id: SAVED_JOB_TOAST_ID,
              duration: SAVED_JOB_TOAST_DURATION,
              classNames: {
                toast: '!rounded-2xl lg:!min-w-[530px]',
              },
              action: createElement(SavedJobToastAction, {
                onDismiss: () => toast.dismiss(SAVED_JOB_TOAST_ID),
              }),
            });
          });
        }

        if (fetchOnMount) {
          void refetch();
        } else {
          void checkSavedJobs([jobId]);
        }
      } catch {
        setSavedJobIds(currentIds => {
          const nextIds = new Set(currentIds);

          if (wasSaved) {
            nextIds.add(jobId);
          } else {
            nextIds.delete(jobId);
          }

          return nextIds;
        });
        void toastPromise.then(toast => {
          toast?.error(
            wasSaved
              ? 'Không thể gỡ việc làm đã lưu. Vui lòng thử lại.'
              : 'Không thể lưu việc làm. Vui lòng thử lại.',
            {
              action: null,
              duration: SAVED_JOB_TOAST_DURATION,
              id: SAVED_JOB_TOAST_ID,
            }
          );
        });
      } finally {
        setPendingJobId(null);
      }
    },
    [
      checkSavedJobs,
      fetchOnMount,
      refetch,
      savedJobIds,
      sourceScreen,
      trackOpportunityEvent,
    ]
  );

  return {
    checkSavedJobs,
    error,
    isLoading,
    pendingJobId,
    pagination,
    refetch,
    savedJobIds,
    savedJobs,
    toggleSavedJob,
  };
}
